/**
 * @fileoverview Illustrated full-screen help panel for the document builder (Phase 5, Workstream J1/J2).
 * @module ui-admin/components/BuilderHelpPanel
 *
 * Responsibilities: - Bind the generic `HelpPanel` to the document-builder help content and
 *                     illustration registry (sections, ToC title, heading, test ids).
 * Dependencies:     - HelpPanel; static builder help content + illustration registry.
 * Architecture:     - Thin wrapper — all rendering/navigation behavior lives in the shared
 *                     `HelpPanel` (single panel implementation, DRY). All content is static
 *                     data bundled with the app — no API, no Dexie, no mock data.
 * Notes:            - Preserves the historical `builder-help-panel` test id and section/ToC
 *                     test ids used by existing tests.
 */

import React from 'react';
import { HelpPanel } from '../HelpPanel/HelpPanel';
import { BUILDER_HELP_SECTIONS } from '../../help/builderHelpContent';
import { ILLUSTRATIONS } from '../../help/illustrations';
import type { HelpSectionId, IllustrationId } from '../../help/types';

/**
 * @contract BuilderHelpPanelProps
 * Purpose: Control the illustrated builder help panel.
 * Fields:
 * - open: whether the panel is shown.
 * - onClose: called on ×, Escape, or backdrop click.
 * - onStartTour: optional; called by "Take the tour →" (closes the panel and starts the tour).
 * - initialSectionId: optional section to open at (deep link from ComponentHelpPopover).
 */
export interface BuilderHelpPanelProps {
  open: boolean;
  onClose: () => void;
  onStartTour?: () => void;
  initialSectionId?: HelpSectionId;
}

/**
 * @component BuilderHelpPanel
 * Purpose:     Full-screen illustrated help system for the document builder.
 * Props:       BuilderHelpPanelProps.
 * Behavior:    Delegates to the generic HelpPanel with the builder content registry.
 * Data Source: static help content (no API/Dexie).
 * Constraints: no mock data; no network.
 */
export function BuilderHelpPanel({ open, onClose, onStartTour, initialSectionId }: BuilderHelpPanelProps) {
  return (
    <HelpPanel<HelpSectionId, IllustrationId>
      open={open}
      onClose={onClose}
      onStartTour={onStartTour}
      initialSectionId={initialSectionId}
      sections={BUILDER_HELP_SECTIONS}
      illustrations={ILLUSTRATIONS}
      tocTitle="Builder Help"
      heading="How the document builder works"
      ariaLabel="Document builder help"
      panelTestId="builder-help-panel"
    />
  );
}

export default BuilderHelpPanel;
